import React, { useMemo, useState } from 'react';
import { IntercomSettingsPreview, SettingValue } from '../../api/intercomSettings';
import { useIntercomSettingsEditor } from '../../hooks/useIntercomSettingsEditor';
import { BulkApplyDialog } from './BulkApplyDialog';
import { EffectiveBehaviorSummary } from './EffectiveBehaviorSummary';
import { SettingsField } from './SettingsField';
import { SettingsSection } from './SettingsSection';
import './IntercomSettingsEditor.css';

type FieldDescriptor = [string, string, 'boolean' | 'string' | 'number', string?];
const SECTIONS: Array<{ key: 'assignment' | 'calling' | 'access' | 'visitor' | 'advanced'; title: string; fields: FieldDescriptor[] }> = [
  { key: 'assignment', title: 'Status & Assignment', fields: [
    ['enabled', 'Intercom enabled', 'boolean'], ['label', 'Intercom name', 'string'], ['phone_number', 'Phone number', 'string'],
  ] },
  { key: 'calling', title: 'Calling', fields: [
    ['allow_phone_call', 'Voice calls', 'boolean'], ['allow_video_call', 'Video calls', 'boolean'], ['simultaneous_ring', 'Ring residents simultaneously', 'boolean'],
    ['allow_door_unlock_in_call', 'In-call unlock', 'boolean'], ['dtmf_unlock_digit', 'DTMF unlock digit', 'string'],
    ['call_timeout_seconds', 'Call timeout (seconds)', 'number'], ['ring_duration_seconds', 'Ring duration (seconds)', 'number'],
    ['voice_greeting_message', 'Phone greeting', 'string'], ['visitor_connecting_message', 'Visitor connecting message', 'string'],
    ['tenant_call_announcement', 'Resident announcement', 'string'], ['no_answer_message', 'No-answer message', 'string'],
  ] },
  { key: 'access', title: 'Access Methods', fields: [
    ['pin_access_enabled', 'Tenant PIN', 'boolean'], ['guest_pin_access_enabled', 'Guest PIN', 'boolean'], ['face_access_enabled', 'Face Access', 'boolean'],
    ['pin_entry_photo_enabled', 'Capture PIN-entry photo', 'boolean'], ['pin_entry_photo_required', 'Require PIN-entry photo', 'boolean'],
  ] },
  { key: 'visitor', title: 'Visitor Experience', fields: [
    ['greeting_message', 'Visitor greeting', 'string'], ['directory_display', 'Directory display', 'string'], ['private_directory', 'Private directory', 'boolean'],
    ['require_visitor_name', 'Require visitor name', 'boolean'], ['require_visitor_phone', 'Require visitor phone', 'boolean'],
    ['hide_vacant_units', 'Hide vacant units', 'boolean'], ['show_floor_grouping', 'Group units by floor', 'boolean'],
    ['show_phone_on_directory', 'Show resident phone', 'boolean'], ['show_tenant_count', 'Show resident count', 'boolean'],
    ['allow_tenant_create_guests', 'Residents can create guests', 'boolean'], ['allow_tenant_create_deliveries', 'Residents can create deliveries', 'boolean'],
    ['delivery_message', 'Delivery message', 'string'], ['delivery_max_uses', 'Delivery pass uses', 'number'],
  ] },
  { key: 'advanced', title: 'Automation & Advanced', fields: [
    ['auto_lock_enabled', 'Auto-lock', 'boolean'], ['auto_lock_start', 'Auto-lock start', 'string'], ['auto_lock_end', 'Auto-lock end', 'string'],
    ['geofence_enabled', 'Geofence', 'boolean'], ['geofence_radius_m', 'Geofence radius (meters)', 'number'], ['record_video_calls', 'Record video calls', 'boolean'],
    ['display_style', 'Display style', 'string'], ['accent_color', 'Accent color', 'string'], ['background_color', 'Background color', 'string'],
    ['logo_url', 'Logo URL', 'string'], ['custom_footer_text', 'Footer text', 'string'], ['show_powered_by', 'Show Powered by Intrlock', 'boolean'],
  ] },
];

export const IntercomSettingsEditor: React.FC<{ intercomId: string }> = ({ intercomId }) => {
  const editor = useIntercomSettingsEditor(intercomId);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ assignment: true, calling: true });
  const [bulkPreview, setBulkPreview] = useState<IntercomSettingsPreview | null>(null);
  const issues = useMemo(() => new Map((editor.document?.effective?.errors ?? []).map((item) => [item.field, item])), [editor.document]);

  if (editor.loading) return <div className="intercom-settings-loading">Loading intercom settings…</div>;
  if (!editor.document) return <div role="alert" className="intercom-settings-error">{editor.error || 'Intercom settings are unavailable.'}</div>;

  const openBulkReview = async () => setBulkPreview(await editor.preview('all_property_intercoms', 'skip_reviewed'));
  return (
    <div className="intercom-settings-editor">
      <EffectiveBehaviorSummary effective={editor.document.effective} />
      {SECTIONS.map((section) => (
        <SettingsSection
          key={section.key}
          title={section.title}
          summary={`${section.fields.filter(([field]) => editor.draft[field] === true).length} enabled`}
          open={Boolean(openSections[section.key])}
          onToggle={() => setOpenSections((current) => ({ ...current, [section.key]: !current[section.key] }))}
        >
          <div className="intercom-settings-fields">
            {section.fields.map(([field, label, type, hint]) => (
              <SettingsField key={field} field={field} label={label} type={type} hint={hint} value={editor.draft[field]} issue={issues.get(field)} onChange={(value: SettingValue) => editor.setValue(field, value)} />
            ))}
          </div>
        </SettingsSection>
      ))}
      {editor.error && <p role="alert" className="intercom-settings-error">{editor.error}</p>}
      <div className="intercom-settings-actions">
        <button type="button" className="secondary" disabled={!editor.dirty || editor.saving} onClick={editor.discard}>Discard</button>
        <button type="button" disabled={!editor.dirty || editor.saving} onClick={() => editor.saveSingle()}>Save this intercom</button>
        {editor.document.property_intercom_count > 1 && (
          <button type="button" className="secondary" disabled={!editor.dirty || editor.saving} onClick={openBulkReview}>Save and apply changes to all…</button>
        )}
      </div>
      {bulkPreview && <BulkApplyDialog open onClose={() => setBulkPreview(null)} onApply={async (token) => { await editor.saveBulk(token); setBulkPreview(null); }} baseline={editor.baseline} changes={editor.changes} preview={bulkPreview} saving={editor.saving} />}
    </div>
  );
};
